import React, {
	Context, useState, useContext, useCallback,
} from 'react';

export interface MediaControlsStateInterface {
	video?: null;
	audio?: null;
	paused?: boolean;
	duration?: number;
	elapsed?: number;
	volume?: number;
	shownVolume?: number;
	scrubbing?: boolean;
	updatePresentedMedia?: null;
	adjustedVolume?: (volume: number) => number;
	muted?: boolean;
	wasEnded?: boolean;
	isForShotEditorModal?: boolean;
	isUseMediaControlsSyncEnabled?: boolean; // Set to true when use-media-controls-sync is actively controlling this media
}

export interface MediaControlsDataInterface {
    send?: Function;
    state?: MediaControlsStateInterface;
	nextPage?: Function;
	previousPage?: Function;
	goToPage?: (page: number) => void;
    id: string;
	from?: string;
}

type MediaControlsDataSet = Record<MediaControlsDataInterface['id'], MediaControlsDataInterface>

export type MediaControlsDataSetState = Record<
	MediaControlsDataInterface['id'],
	MediaControlsDataInterface & MediaControlsStateInterface
>

export interface MediaControlsHookInterface {
	addMediaControls: (newData: MediaControlsDataInterface) => void;
	batchUpdateMediaControls: (updates: MediaControlsDataInterface[]) => void;
	getMediaControlsForId: (id: string) => MediaControlsDataInterface;
	getAllMediaControls: () => MediaControlsDataSetState;
	removeMediaControlsById: (id: string) => void;
}

export const MediaControlsContext = React.createContext({
	addMediaControls: () => ({}),
	batchUpdateMediaControls: () => undefined,
	getMediaControlsForId: () => ({}),
	getAllMediaControls: () => ({}),
	removeMediaControlsById: () => undefined,
}) as unknown as Context<MediaControlsHookInterface>;

export function useMediaControls(): MediaControlsHookInterface {
	const [mcData, setMCData] = useState<MediaControlsDataSet>({});

	const addMediaControls = useCallback((newData: MediaControlsDataInterface): void => {
		const id = `${newData.id}${newData.state?.isForShotEditorModal ? '-draft' : ''}`;
		setMCData((prev: MediaControlsDataSet) => ({
			...prev,
			[`mc-${id}`]: {
				...prev[`mc-${id}`],
				...newData,
				state: {
					...prev[`mc-${id}`]?.state,
					...newData?.state,
				},
			},
		}));
	}, []);

	const batchUpdateMediaControls = useCallback((updates: MediaControlsDataInterface[]): void => {
		setMCData((prev: MediaControlsDataSet) => {
			const updated = { ...prev };
			updates.forEach((newData) => {
				const id = `${newData.id}${newData.state?.isForShotEditorModal ? '-draft' : ''}`;
				updated[`mc-${id}`] = {
					...updated[`mc-${id}`],
					...newData,
					state: {
						...updated[`mc-${id}`]?.state,
						...newData?.state,
					},
				};
			});
			return updated;
		});
	}, []);

	const removeMediaControlsById = useCallback((id: string): void => {
		setMCData((prev: MediaControlsDataSet) => {
			const newSet = prev;
			delete newSet[`mc-${id}`];
			return newSet;
		});
	}, []);

	const getMediaControlsForId = (id: string): MediaControlsDataInterface => mcData[id];

	const getAllMediaControls = (): MediaControlsDataSetState => (
		mcData as MediaControlsDataSetState
	);

	return {
		addMediaControls,
		batchUpdateMediaControls,
		getMediaControlsForId,
		getAllMediaControls,
		removeMediaControlsById,
	};
}

export const useGetMediaControls = (id: string = '') => {
	const { getMediaControlsForId } = useContext(MediaControlsContext);
	return getMediaControlsForId(`mc-${id}`);
};

export const useGetAllMediaControls = (): MediaControlsDataSetState => {
	const { getAllMediaControls } = useContext(MediaControlsContext);
	return getAllMediaControls() as MediaControlsDataSetState;
};

export const MediaControlsProvider = (
	props: any,
): React.ReactElement<any, React.Provider<MediaControlsHookInterface>> => {
	const MediaControlsContextValue = useMediaControls();
	return <MediaControlsContext.Provider value={MediaControlsContextValue}>
		{props.children}
	</MediaControlsContext.Provider>;
};
